import { action, atom, withInit } from '@reatom/framework'

import { nativeNetworkCurrencyAtom } from '@root/entities/currency'
import { reatomCurrencyInput } from '@root/features/currency/currency-input-reatom'

import type { Ctx } from '@reatom/framework'
import type { CurrencyModel } from '@root/entities/currency'
import type { Quote } from '@root/entities/quote'
import type { WalletModel } from '@root/entities/wallet/atoms'

export const reatomWalletSwapInput = (wallet: WalletModel) => {
  const walletAtom = atom(wallet, 'walletAtom')
  const inCurrencyAtom = atom<CurrencyModel | undefined>(
    undefined,
    'inCurrencyAtom',
  ).pipe(
    withInit((ctx) => {
      const nativeToken = ctx.get(nativeNetworkCurrencyAtom)
      return nativeToken
    }),
  )

  const outCurrencyAtom = atom<CurrencyModel | undefined>(
    undefined,
    'outCurrencyAtom',
  )

  const inCurrencyInputModel = reatomCurrencyInput(
    {
      currencyAtom: inCurrencyAtom,
      walletAtom: walletAtom,
    },
    'inCurrencyInputModel',
  )
  const outCurrencyInputModel = reatomCurrencyInput(
    {
      currencyAtom: outCurrencyAtom,
      walletAtom: walletAtom,
    },
    'outCurrencyInputModel',
  )

  const inputTypeAtom = atom<Quote.QuoteType>('EXACT_INPUT', 'inputTypeAtom')

  const inputCurrencyAtom = atom((ctx) => {
    const inputSource = ctx.spy(inputTypeAtom)
    return inputSource === 'EXACT_INPUT'
      ? ctx.spy(inCurrencyAtom)
      : ctx.spy(outCurrencyAtom)
  }, 'inputCurrencyAtom')

  const inputAmountAtom = atom((ctx) => {
    const inputSource = ctx.spy(inputTypeAtom)
    return inputSource === 'EXACT_INPUT'
      ? ctx.spy(inCurrencyInputModel.amountAtom)
      : ctx.spy(outCurrencyInputModel.amountAtom)
  }, 'inputAmountAtom')

  const switchCurrenciesAction = action((ctx) => {
    const from = ctx.get(inCurrencyAtom)
    const to = ctx.get(outCurrencyAtom)
    inCurrencyAtom(ctx, to)
    outCurrencyAtom(ctx, from)
  }, 'switchCurrenciesAction')

  const setInCurrencyAction = action((ctx: Ctx, currency: CurrencyModel) => {
    const quoteCurrency = ctx.get(outCurrencyAtom)
    if (quoteCurrency === currency) {
      outCurrencyAtom(ctx, undefined)
    }
    inCurrencyAtom(ctx, currency)
  }, 'setInCurrencyAction')

  const setOutCurrencyAction = action((ctx, currency: CurrencyModel) => {
    const baseCurrency = ctx.get(inCurrencyAtom)
    if (baseCurrency === currency) {
      inCurrencyAtom(ctx, undefined)
    }
    outCurrencyAtom(ctx, currency)
  }, 'setOutCurrencyAction')

  const currenciesAtom = atom((ctx) => {
    const inCurrency = ctx.spy(inCurrencyAtom)
    const outCurrency = ctx.spy(outCurrencyAtom)
    return { inCurrency, outCurrency }
  }, 'currenciesAtom')

  return {
    currenciesAtom,
    inputTypeAtom,
    setInCurrencyAction,
    setOutCurrencyAction,
    inCurrencyAtom,
    outCurrencyAtom,
    switchCurrenciesAction,
    inputCurrencyAtom,
    inputAmountAtom,
    inCurrencyInputModel,
    outCurrencyInputModel,
  }
}

export type WalletSwapInputModel = ReturnType<typeof reatomWalletSwapInput>
